import {
    ActionIcon,
    Badge,
    Button,
    Card,
    Flex,
    Group,
    LoadingOverlay,
    Paper,
    Select,
    SimpleGrid,
    Stack,
    Switch,
    Table,
    Text,
    TextInput,
    Title,
} from '@mantine/core'
import { IconArchive, IconRefresh, IconSearch, IconTrash } from '@tabler/icons-react'
import dayjs from 'dayjs'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
    archiveLmbListing,
    deleteLmbListing,
    getLmbCmsListings,
    getLmbCmsStatistics,
} from 'src/apis/yourListAPI'
import { formatVNDCompact } from 'src/utils/utils'

interface ListingRow {
    id: string
    name: string
    status: string
    location: string
    category: string
    createdAt: string
    updatedAt: string
    raw: any
}

const PAGE_SIZE_OPTIONS = [
    { label: '10 / page', value: '10' },
    { label: '20 / page', value: '20' },
    { label: '50 / page', value: '50' },
]

const STATUS_OPTIONS = [
    { label: 'All', value: 'all' },
    { label: 'Published', value: 'PUBLISHED' },
    { label: 'Archived', value: 'ARCHIVED' },
    { label: 'Inactive', value: 'INACTIVE' },
    { label: 'Hidden', value: 'HIDDEN' },
    { label: 'Undefined', value: null },
]

const formatDate = (value?: string) => {
    if (!value) return '-'
    const parsed = dayjs(value)
    return parsed.isValid() ? parsed.format('DD/MM/YYYY HH:mm') : '-'
}

const getRowsAndCount = (response: any) => {
    const objects = response?.data?.results?.objects
    const rows = Array.isArray(objects?.rows) ? objects.rows : []
    const count = Number(objects?.count ?? response?.data?.pagination?.total ?? rows.length ?? 0)
    return { rows, count }
}

const toText = (value: any): string => {
    if (value === null || value === undefined || value === '') return '-'
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        return String(value)
    }
    if (Array.isArray(value)) {
        const parts = value.map((x) => toText(x)).filter((x) => x !== '-')
        return parts.length ? parts.join(', ') : '-'
    }
    if (typeof value === 'object') {
        if (value.title) return toText(value.title)
        if (value.name) return toText(value.name)
        if (value.label) return toText(value.label)
        return '-'
    }
    return '-'
}

const normalizeRow = (item: any): ListingRow => {
    return {
        id: toText(item?.id),
        name: toText(item?.item_name ?? item?.name ?? item?.title),
        status: toText(item?.status),
        location: toText(item?.address ?? item?.location),
        category: toText(item?.category?.title ?? item?.category_name ?? item?.category),
        createdAt: formatDate(item?.created_at),
        updatedAt: formatDate(item?.updated_at),
        raw: item,
    }
}

const formatStatValue = (key: string, value: string | number) => {
    if (value === null || value === undefined || value === '') return '-'

    const num =
        typeof value === 'number'
            ? value
            : Number(String(value).replace(/,/g, '').trim())

    const isNumber = !Number.isNaN(num)

    if (key === 'total_earnings' && isNumber) {
        // return num.toLocaleString('vi-VN') + ' VND'
        return formatVNDCompact(num)
    }

    if (isNumber) {
        return num.toLocaleString('vi-VN')
    }

    return String(value)
}
const SORT_OPTIONS = [
    { label: 'Created at', value: 'created_at' },
    { label: 'Price asc', value: 'price_asc' },
    { label: 'Price desc', value: 'price_desc' },
]
function YourBizPage() {
    const [rows, setRows] = useState<ListingRow[]>([])
    const [total, setTotal] = useState(0)
    const [loading, setLoading] = useState(false)
    const [actionLoadingId, setActionLoadingId] = useState<string | null>(null)

    const [statistics, setStatistics] = useState<Record<string, string | number>>({})

    const [query, setQuery] = useState('')
    const [status, setStatus] = useState('PUBLISHED')
    const [location, setLocation] = useState('')
    const [categoriesText, setCategoriesText] = useState('')
    const [sortValue, setSortValue] = useState('created_at')
    const [paranoidValue, setParanoidValue] = useState(false)


    const [page, setPage] = useState(1)
    const [limit, setLimit] = useState(10)
    const fetchStatistics = useCallback(async () => {
        const res = await getLmbCmsStatistics()
        const statsObject = res?.data?.results?.object || {}

        const normalized = Object.fromEntries(
            Object.entries(statsObject).map(([key, value]) => {
                if (typeof value === 'string') {
                    const trimmed = value.trim()
                    const asNumber = Number(trimmed)
                    if (trimmed !== '' && !Number.isNaN(asNumber)) {
                        return [key, asNumber]
                    }
                }
                return [key, value]
            }),
        ) as Record<string, string | number>

        setStatistics(normalized)
    }, [])

    const fetchList = useCallback(async () => {
        setLoading(true)
        try {
            const categories = categoriesText
                .split(',')
                .map((item) => item.trim())
                .filter(Boolean)
            const res = await getLmbCmsListings({
                page,
                limit,
                query: query || undefined,
                status: status === 'all' ? undefined : status,
                location: location || undefined,
                categories: categories.length ? categories : undefined,
                sort: sortValue || undefined,
                paranoid: paranoidValue,
            })

            const { rows: apiRows, count } = getRowsAndCount(res)
            setRows(apiRows.map(normalizeRow))
            setTotal(count)
        } finally {
            setLoading(false)
        }
    }, [categoriesText, limit, location, page, query, status, sortValue, paranoidValue])

    const refreshPage = useCallback(async () => {
        await Promise.all([fetchStatistics(), fetchList()])
    }, [fetchList, fetchStatistics])

    useEffect(() => {
        refreshPage()
    }, [refreshPage])

    const totalPages = useMemo(() => {
        if (!total || !limit) return 1
        return Math.max(1, Math.ceil(total / limit))
    }, [limit, total])

    const handleArchive = useCallback(
        async (id: string) => {
            if (!id) return
            const confirmed = window.confirm('Archive listing này?')
            if (!confirmed) return

            setActionLoadingId(id)
            try {
                await archiveLmbListing(id)
                await refreshPage()
            } finally {
                setActionLoadingId(null)
            }
        },
        [refreshPage],
    )

    const handleDelete = useCallback(
        async (id: string) => {
            if (!id) return
            const confirmed = window.confirm('Xóa listing này?')
            if (!confirmed) return

            setActionLoadingId(id)
            try {
                await deleteLmbListing(id)
                await refreshPage()
            } finally {
                setActionLoadingId(null)
            }
        },
        [refreshPage],
    )

    return (
        <Stack spacing={16}>
            <Card withBorder radius="md" p="md">
                <Flex justify="space-between" align="center" wrap="wrap" gap={12}>
                    <Stack spacing={2}>
                        <Title order={4}>You BIZ</Title>
                        <Text color="dimmed" size="sm">
                            Manage listing data from CMS endpoints.
                        </Text>
                    </Stack>
                    <Button
                        variant="default"
                        leftIcon={<IconRefresh size={16} />}
                        onClick={refreshPage}
                        loading={loading}
                    >
                        Refresh
                    </Button>
                </Flex>
            </Card>

            <SimpleGrid cols={6} spacing="sm">
                {Object.keys(statistics).length ? (
                    Object.entries(statistics)
                        .slice(0, 20)
                        .map(([key, value]) => (
                            <Card key={key} withBorder radius="md" p="md">
                                <Text size="sm" color="dimmed" tt="capitalize">
                                    {key.replace(/_/g, ' ')}
                                </Text>
                                <Text size={20} fw={700}>
                                    {formatStatValue(key, value) || '-'}
                                </Text>
                                {/* <Text size="sm" color="dimmed">
                                    {value}
                                </Text> */}
                            </Card>
                        ))
                ) : (
                    <Card withBorder radius="md" p="md">
                        <Text color="dimmed">No statistics data.</Text>
                    </Card>
                )}
            </SimpleGrid>

            <Paper withBorder radius="md" p="md" pos="relative">
                <LoadingOverlay visible={loading} />

                <Stack spacing={12}>
                    <Flex gap={10} wrap="wrap" align="flex-end">
                        <TextInput
                            label="Search"
                            placeholder="ID, name..."
                            value={query}
                            onChange={(e) => {
                                setQuery(e.currentTarget.value)
                                setPage(1)
                            }}
                            icon={<IconSearch size={16} />}
                        />
                        <Select
                            label="Status"
                            data={STATUS_OPTIONS}
                            value={status}
                            onChange={(value) => {
                                setStatus(value || 'all')
                                setPage(1)
                            }}
                            w={180}
                        />
                        <TextInput
                            label="Location"
                            placeholder="HCM, HN..."
                            value={location}
                            onChange={(e) => {
                                setLocation(e.currentTarget.value)
                                setPage(1)
                            }}
                        />
                        <TextInput
                            label="Categories"
                            placeholder="food, coffee, hotel"
                            value={categoriesText}
                            onChange={(e) => {
                                setCategoriesText(e.currentTarget.value)
                                setPage(1)
                            }}
                        />
                        <Select
                            label="Sort"
                            data={SORT_OPTIONS}
                            value={sortValue}
                            onChange={(value) => {
                                setSortValue(value || 'created_at')
                                setPage(1)
                            }}
                            w={180}
                        />
                        <Switch
                            label="Find deleted records"
                            checked={paranoidValue}
                            onChange={(event) => {
                                setParanoidValue(event.currentTarget.checked)
                                setPage(1)
                            }}
                        />
                    </Flex>

                    <Table striped highlightOnHover withColumnBorders>
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Name</th>
                                <th>Status</th>
                                <th>Location</th>
                                <th>Category</th>
                                {/* <th>Created</th> */}
                                <th>Updated</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row, rowIndex) => {
                                const rowLoading = actionLoadingId === row.id
                                return (
                                    <tr key={row.id}>
                                        {/* <td>{row.id || '-'}</td> */}
                                        <td>{(page - 1) * limit + rowIndex + 1}</td>
                                        <td>{row.name}</td>
                                        <td>
                                            <Badge variant="light">{row.status}</Badge>
                                        </td>
                                        <td>{row.location}</td>
                                        <td>{row.category}</td>
                                        {/* <td>{row.createdAt}</td> */}
                                        <td>{row.updatedAt}</td>
                                        <td>
                                            <Group spacing={6}>
                                                <ActionIcon
                                                    variant="light"
                                                    color="blue"
                                                    onClick={() => handleArchive(row.id)}
                                                    loading={rowLoading}
                                                >
                                                    <IconArchive size={16} />
                                                </ActionIcon>
                                                <ActionIcon
                                                    variant="light"
                                                    color="red"
                                                    onClick={() => handleDelete(row.id)}
                                                    loading={rowLoading}
                                                >
                                                    <IconTrash size={16} />
                                                </ActionIcon>
                                            </Group>
                                        </td>
                                    </tr>
                                )
                            })}
                            {rows.length === 0 && !loading && (
                                <tr>
                                    <td colSpan={8}>
                                        <Text size="sm" color="dimmed" align="center">
                                            No data
                                        </Text>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>

                    <Flex justify="space-between" align="center" wrap="wrap" gap={8}>
                        <Group>
                            <Button
                                variant="default"
                                disabled={page <= 1 || loading}
                                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                            >
                                Prev
                            </Button>
                            <Button
                                variant="default"
                                disabled={page >= totalPages || loading}
                                onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                            >
                                Next
                            </Button>
                            <Text size="sm" color="dimmed">
                                Page {page}/{totalPages} - Total {total}
                            </Text>
                        </Group>

                        <Select
                            label="Page size"
                            data={PAGE_SIZE_OPTIONS}
                            value={String(limit)}
                            onChange={(value) => {
                                const next = Number(value || 10)
                                setLimit(next)
                                setPage(1)
                            }}
                            w={140}
                        />
                    </Flex>
                </Stack>
            </Paper>
        </Stack>
    )
}

export default YourBizPage